import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/authService';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Alert from '../components/ui/Alert';
import { useAuth } from '../contexts/AuthContext';
import './AccountPage.css';

const AccountPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, updateProfile, signOut } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState(null);
  
  // Form state
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Form validation errors
  const [errors, setErrors] = useState({});
  
  // Load user data
  useEffect(() => {
    if (isAuthenticated && user) {
      setEmail(user.email || '');
      setFullName(user.user_metadata?.full_name || '');
    }
  }, [isAuthenticated, user]);
  
  // Validate profile form
  const validateProfileForm = () => {
    const newErrors = {};
    
    if (!fullName) {
      newErrors.fullName = 'Full name is required';
    }
    
    setErrors(prev => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };
  
  // Validate password form
  const validatePasswordForm = () => {
    const newErrors = {};
    
    if (!currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }
    
    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }
    
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(prev => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle profile update
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    if (!validateProfileForm()) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Update user metadata
      await updateProfile({
        full_name: fullName
      });
      
      setSuccess('Profile updated successfully');
      setLoading(false);
      
      // Clear success message after a delay
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile');
      setLoading(false);
    }
  };
  
  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // First verify the current password by signing in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password: currentPassword
      });
      
      if (signInError) {
        throw new Error('Current password is incorrect');
      }
      
      // Update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (updateError) {
        throw updateError;
      }
      
      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      setSuccess('Password updated successfully');
      setLoading(false);
      
      // Clear success message after a delay
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error('Error changing password:', err);
      setError(err.message || 'Failed to change password');
      setLoading(false);
    }
  };
  
  // Handle account deletion
  const handleDeleteAccount = async () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      try {
        setLoading(true);
        setError(null);
        
        // Get the current session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          throw new Error('No active session found');
        }
        
        // Call the Netlify function to delete the account
        const response = await fetch('/.netlify/functions/delete-account', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        });
        
        const result = await response.json();
        
        if (!response.ok) {
          throw new Error(result.error || 'Failed to delete account');
        }
        
        // Sign out the user
        await signOut();
        
        // Redirect to home page
        navigate('/');
      } catch (err) {
        console.error('Error deleting account:', err);
        setError(err.message || 'Failed to delete account');
        setLoading(false);
      }
    }
  };
  
  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return (
      <div className="account-page">
        <div className="container">
          <Card className="redirect-card" title="" subtitle="" footer="">
            <h2>Please Sign In</h2>
            <p>You need to be signed in to access your account settings.</p>
            <Button onClick={() => navigate('/login')}>
              Sign In
            </Button>
          </Card>
        </div>
      </div>
    );
  }
  
  return (
    <div className="account-page">
      <div className="container">
        <h1 className="page-title">My Account</h1>
        
        {error && (
          <Alert 
            type="error" 
            title="Error"
            message={error} 
            dismissible={true}
            onDismiss={() => setError(null)}
            icon={null}
            action={null}
          />
        )}
        
        {success && (
          <Alert 
            type="success" 
            title="Success"
            message={success} 
            dismissible={true}
            onDismiss={() => setSuccess('')}
            icon={null}
            action={null}
          />
        )}
        
        <div className="account-sections">
          {/* Profile Section */}
          <Card className="account-section" title="" subtitle="" footer="">
            <h2 className="section-title">Profile Information</h2>
            
            <form onSubmit={handleProfileUpdate} className="account-form">
              <div className="form-group">
                <Input
                  label="Email Address"
                  type="email"
                  id="email"
                  value={email}
                  disabled={true}
                  helpText="Email cannot be changed"
                />
              </div>
              
              <div className="form-group">
                <Input
                  label="Full Name"
                  type="text"
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  error={errors.fullName}
                  required
                />
              </div>
              
              <div className="form-actions">
                <Button 
                  type="submit" 
                  loading={loading}
                  disabled={loading}
                >
                  Update Profile
                </Button>
              </div>
            </form>
          </Card>
          
          {/* Password Section */}
          <Card className="account-section" title="" subtitle="" footer="">
            <h2 className="section-title">Change Password</h2>
            
            <form onSubmit={handlePasswordChange} className="account-form">
              <div className="form-group">
                <Input
                  label="Current Password"
                  type="password"
                  id="currentPassword"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  error={errors.currentPassword}
                  required
                />
              </div>
              
              <div className="form-group">
                <Input
                  label="New Password"
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  error={errors.newPassword}
                  required
                />
              </div>
              
              <div className="form-group">
                <Input
                  label="Confirm New Password"
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={errors.confirmPassword}
                  required
                />
              </div>
              
              <div className="form-actions">
                <Button 
                  type="submit" 
                  loading={loading}
                  disabled={loading}
                >
                  Change Password
                </Button>
              </div>
            </form>
          </Card>
          
          {/* Danger Zone */}
          <Card className="account-section danger-zone" title="" subtitle="" footer="">
            <h2 className="section-title">Danger Zone</h2>
            
            <div className="danger-actions">
              <div className="danger-action">
                <div className="danger-info">
                  <h3>Delete Account</h3>
                  <p>
                    This will permanently delete your account and all associated data.
                    This action cannot be undone.
                  </p>
                </div>
                
                <Button 
                  variant="danger" 
                  onClick={handleDeleteAccount}
                  disabled={loading}
                >
                  Delete Account
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
